import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEventHandler,
  type MutableRefObject,
  type ReactNode,
  type Ref
} from 'react';
import { createPortal } from 'react-dom';

/**
 * Trigger-anchored dropdown panel with caller-supplied content. The trigger is
 * a render-prop the caller composes around an `<IconButton>` / `<Button>` —
 * spread the supplied api onto it. `Popover` owns the open state, the
 * absolute-positioned body, Escape close, and outside-click close.
 *
 * `<Menu>` composes this primitive with `role="menu"` and a fixed item list.
 * For content-agnostic bodies (filter panes, property pickers, version
 * history, …) use the default `role="dialog"`.
 *
 * 14.3f shipped this primitive for the DocumentEditor inspector's
 * archived-attribute clock affordance (⌚ → list of
 * `custom_attribute_value_versions` rows with per-row Restore button).
 */

export type DropdownRole = 'dialog' | 'menu';

export type PopoverStrategy = 'absolute' | 'fixed';

/** Vertical edge the popover opens toward, relative to the trigger. */
export type PopoverSide = 'top' | 'bottom';

export interface PopoverTriggerApi {
  onClick: () => void;
  'aria-haspopup': DropdownRole;
  'aria-expanded': boolean;
}

export interface PopoverProps {
  /** Render the trigger element; spread the supplied api onto it. */
  trigger: (api: PopoverTriggerApi) => ReactNode;
  /** Popover body. Receives `close` so inner controls can dismiss the
   *  popover (e.g. after a Restore click). */
  children: (api: { close: () => void }) => ReactNode;
  /** Accessible label for the panel. */
  label: string;
  /** ARIA role for the panel. Default `dialog`; `<Menu>` passes `menu`. */
  role?: DropdownRole;
  /** Horizontal edge the popover aligns to. Default `end`. */
  align?: 'start' | 'end';
  /** Vertical edge the popover opens toward. Default `bottom` (opens
   *  downward). Pass `top` to open upward — e.g. a trigger near the bottom of
   *  its scrollport whose downward panel would be clipped/offscreen. */
  side?: PopoverSide;
  /** `fixed` portals the panel to `document.body` so it is not clipped by
   *  overflow scrollports or painted under later canvas siblings. */
  strategy?: PopoverStrategy;
  className?: string;
  'data-testid'?: string;
  /** Ref attached to the panel element (used by `<Menu>` for roving focus). */
  panelRef?: Ref<HTMLDivElement>;
  /** Key handler on the panel (used by `<Menu>` for ArrowUp/ArrowDown). */
  onPanelKeyDown?: KeyboardEventHandler<HTMLDivElement>;
  /** Fires whenever the open state changes. */
  onOpenChange?: (open: boolean) => void;
  /** Fires after the panel mounts (use for roving-focus seeding in `<Menu>`). */
  onPanelOpened?: () => void;
}

function assignPanelRef(
  node: HTMLDivElement | null,
  internalRef: MutableRefObject<HTMLDivElement | null>,
  externalRef?: Ref<HTMLDivElement>
): void {
  internalRef.current = node;
  if (!externalRef || typeof externalRef === 'function') {
    if (typeof externalRef === 'function') externalRef(node);
    return;
  }
  (externalRef as MutableRefObject<HTMLDivElement | null>).current = node;
}

function readPopoverGapPx(anchor: HTMLElement): number {
  const raw = getComputedStyle(anchor).getPropertyValue('--space-2').trim();
  const parsed = Number.parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : 8;
}

export function Popover({
  trigger,
  children,
  label,
  role = 'dialog',
  align = 'end',
  side = 'bottom',
  strategy = 'absolute',
  className,
  'data-testid': testId,
  panelRef,
  onPanelKeyDown,
  onOpenChange,
  onPanelOpened
}: PopoverProps) {
  const [open, setOpen] = useState(false);
  const [fixedCoords, setFixedCoords] = useState<{ top: number; left: number } | null>(
    null
  );
  const wrapRef = useRef<HTMLDivElement>(null);
  const internalPanelRef = useRef<HTMLDivElement | null>(null);

  const close = useCallback(() => {
    setOpen(false);
    onOpenChange?.(false);
  }, [onOpenChange]);

  const toggle = useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      onOpenChange?.(next);
      return next;
    });
  }, [onOpenChange]);

  const repositionFixed = useCallback(() => {
    const anchor = wrapRef.current;
    const panel = internalPanelRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const gap = readPopoverGapPx(anchor);
    let left = rect.left;
    if (align === 'end' && panel) {
      left = rect.right - panel.offsetWidth;
    }
    const top =
      side === 'top' && panel
        ? rect.top - gap - panel.offsetHeight
        : rect.bottom + gap;
    setFixedCoords({ top, left });
  }, [align, side]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: globalThis.KeyboardEvent) {
      if (e.key === 'Escape') close();
    }
    function onPointer(e: MouseEvent) {
      const target = e.target as Node | null;
      if (!target) return;
      if (wrapRef.current?.contains(target)) return;
      if (internalPanelRef.current?.contains(target)) return;
      close();
    }
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onPointer);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onPointer);
    };
  }, [open, close]);

  useLayoutEffect(() => {
    if (!open) {
      setFixedCoords(null);
      return;
    }
    if (strategy !== 'fixed') {
      onPanelOpened?.();
      return;
    }
    repositionFixed();
    onPanelOpened?.();
    window.addEventListener('scroll', repositionFixed, true);
    window.addEventListener('resize', repositionFixed);
    return () => {
      window.removeEventListener('scroll', repositionFixed, true);
      window.removeEventListener('resize', repositionFixed);
    };
  }, [open, strategy, repositionFixed, onPanelOpened]);

  const cls = ['pn-dropdown-wrap', 'pn-popover-wrap', className]
    .filter(Boolean)
    .join(' ');

  const panelCls = [
    'pn-dropdown',
    role === 'menu' ? 'pn-menu' : 'pn-popover',
    strategy === 'fixed' ? 'pn-popover-fixed' : null,
    strategy === 'absolute' ? `pn-dropdown-${align}` : null,
    strategy === 'absolute'
      ? role === 'menu'
        ? `pn-menu-${align}`
        : `pn-popover-${align}`
      : null,
    // Vertical placement: only the absolute strategy positions via CSS classes
    // (the fixed strategy computes `top` from the measured panel height). Apply
    // the upward marker only for `top` so default (downward) callers keep the
    // existing single-class output.
    strategy === 'absolute' && side === 'top' ? 'pn-dropdown-side-top' : null
  ]
    .filter(Boolean)
    .join(' ');

  const panelStyle =
    strategy === 'fixed' && fixedCoords
      ? { top: fixedCoords.top, left: fixedCoords.left }
      : undefined;

  const panel = open ? (
    <div
      ref={(node) => assignPanelRef(node, internalPanelRef, panelRef)}
      className={panelCls}
      role={role}
      aria-label={label}
      data-side={side}
      data-testid={testId}
      style={panelStyle}
      onKeyDown={onPanelKeyDown}
    >
      {children({ close })}
    </div>
  ) : null;

  return (
    <div ref={wrapRef} className={cls}>
      {trigger({
        onClick: toggle,
        'aria-haspopup': role,
        'aria-expanded': open
      })}
      {strategy === 'fixed' && panel
        ? createPortal(panel, document.body)
        : panel}
    </div>
  );
}
